/**
 * WhatsApp UI color tokens (beautiful-ui).
 *
 * Filled CTAs / nav active use a deeper teal-mint so white labels stay readable
 * (bright neon mint + white flashes and fails contrast). Soft chips / borders
 * keep a lighter mint tint at low opacity.
 *
 * Exceptions (do not use waBtn.primary):
 * - Meta Connect → waBtn.meta
 * - WhatsApp chat chrome / support FAB → WA_WHATSAPP_* only
 * - Decorative plan accents (Starter purple / Growth amber) — not buttons; selected plan uses mint
 *
 * Excluded pages (leave untouched): OptInOptOut, AccountHealth
 *
 * @see `.cursor/rules/prodyt-ui-theming.mdc`
 */

/** Raw color values (for rare inline needs). */
export const WA_COLOR = Object.freeze({
  /** Filled CTA / nav background — deeper for white-text contrast */
  primary: "hsl(158, 48%, 38%)",
  primaryHover: "hsl(158, 48%, 32%)",
  primaryFg: "#ffffff",
  /** Accent for icons/links on light surfaces */
  primaryMuted: "hsl(158, 48%, 34%)",
  /** Soft tint base (chips, borders, focus) — not for filled buttons */
  accent: "hsl(158, 48%, 42%)",
  meta: "#1877F2",
  metaHover: "#166FE5",
  whatsapp: "#25D366",
  whatsappDark: "#075E54",
});

/**
 * Filled / outline button class bundles.
 * Compose with size utilities: `className={cn(waBtn.primary, "h-10 px-4")}`
 */
export const waBtn = Object.freeze({
  primary:
    "bg-[hsl(158,48%,38%)] hover:bg-[hsl(158,48%,32%)] text-white font-semibold disabled:opacity-50",
  secondary:
    "border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800",
  ghost:
    "text-gray-700 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-800",
  destructive:
    "bg-red-600 hover:bg-red-700 text-white font-semibold disabled:opacity-50",
  destructiveGhost:
    "text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40",
  meta: "bg-[#1877F2] hover:bg-[#166FE5] text-white font-semibold disabled:opacity-50",
  link: "text-[hsl(158,48%,32%)] dark:text-[hsl(158,45%,55%)] font-medium hover:underline",
});

/** Segmented tabs / pill triggers (Billing, similar). */
export const waTab = Object.freeze({
  trigger:
    "px-4 py-2 text-sm font-medium rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 data-[state=active]:bg-[hsl(158,48%,38%)] data-[state=active]:text-white data-[state=active]:hover:bg-[hsl(158,48%,32%)] transition-colors",
  underlineActive:
    "border-[hsl(158,48%,38%)] text-[hsl(158,48%,34%)] dark:text-[hsl(158,45%,55%)]",
  underlineInactive:
    "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white",
});

/** Binary toggles (Monthly / Yearly, type pills). */
export const waToggle = Object.freeze({
  selected: "bg-[hsl(158,48%,38%)] text-white shadow-md font-medium",
  unselected:
    "bg-transparent text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium",
});

/** Soft tint surfaces / chips. */
export const waSoft = Object.freeze({
  bg: "bg-[hsl(158,48%,38%)]/10 dark:bg-[hsl(158,48%,42%)]/15",
  text: "text-[hsl(158,48%,34%)] dark:text-[hsl(158,45%,55%)]",
  border: "border-[hsl(158,48%,38%)]/25 dark:border-[hsl(158,48%,42%)]/30",
  chip: "bg-[hsl(158,48%,38%)]/10 text-[hsl(158,48%,34%)] dark:bg-[hsl(158,48%,42%)]/15 dark:text-[hsl(158,45%,55%)]",
});

/** Focus ring for inputs / selects. */
export const waFocusRing =
  "focus:ring-2 focus:ring-[hsl(158,48%,38%)] focus:border-[hsl(158,48%,38%)] dark:focus:ring-[hsl(158,45%,55%)]";

/** Switch checked track. */
export const waSwitchChecked = "data-[state=checked]:bg-[hsl(158,48%,38%)]";

/** Nav / sidebar active (same as primary fill). */
export const waNavActive = "bg-[hsl(158,48%,38%)] text-white";
